import React, { useEffect, useState } from 'react';
import { LiveMatch, MatchRequest, Streamer, getMatches, getMatchPosterUrl, getMatchRequests, createMatchRequest, getStreamers, getSmsReminder, setSmsReminder, updateProfile, SmsReminder } from './api';

function fmtDateLong(s: string) {
  try {
    const d = new Date(s.replace(' ', 'T'));
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return s; }
}

function fmtTime(s: string) {
  try {
    const d = new Date(s.replace(' ', 'T'));
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function fmtCountdown(s: string): string {
  try {
    const diff = new Date(s.replace(' ', 'T')).getTime() - Date.now();
    if (diff < 0) return '';
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    if (days > 0) return `dans ${days}j ${hours}h`;
    if (hours > 0) return `dans ${hours}h ${mins}min`;
    return `dans ${mins} min`;
  } catch { return ''; }
}

const TYPE_LABEL: Record<string, string> = {
  officiel: '🏆 Officiel',
  anniversaire: '🎂 Anniversaire',
  autre: '✏️ Personnalisé',
};
const TYPE_COLOR: Record<string, string> = {
  officiel: '#7c3aed',
  anniversaire: '#ea580c',
  autre: '#0369a1',
};

const STATUS_STYLE: Record<string, { label: string; bg: string; color: string }> = {
  pending:  { label: '⏳ En attente',  bg: '#fef9c3', color: '#a16207' },
  accepted: { label: '✅ Acceptée',    bg: '#f0fdf4', color: '#16a34a' },
  rejected: { label: '❌ Refusée',     bg: '#fef2f2', color: '#dc2626' },
};

function MiniAvatar({ url, name, size = 44 }: { url?: string | null; name?: string | null; size?: number }) {
  const [err, setErr] = useState(false);
  const initial = (name || '?').replace(/\s/g, '').charAt(0).toUpperCase();
  const src = url && /^https?:\/\//i.test(url)
    ? `/api/gift-image?url=${encodeURIComponent(url)}` : url;
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: '#e2e8f0', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.round(size * 0.38), fontWeight: 700, color: '#64748b' }}>
      {src && !err
        ? <img src={src} alt={name || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setErr(true)} />
        : initial}
    </div>
  );
}

type Result = 'win' | 'loss' | 'draw';
const RESULT_STYLE: Record<Result, { label: string; bg: string; color: string; border: string }> = {
  win:  { label: '🏆 Victoire', bg: '#f0fdf4', color: '#16a34a', border: '#16a34a' },
  loss: { label: '😔 Défaite',  bg: '#fef2f2', color: '#dc2626', border: '#dc2626' },
  draw: { label: '🤝 Égalité',  bg: '#fefce8', color: '#d97706', border: '#d97706' },
};

function MatchCard({ m, streamerId, selected, onClick }: { m: LiveMatch; streamerId: number; selected: boolean; onClick: () => void }) {
  const isChal1 = m.streamer1_id === streamerId;
  const myScore = isChal1 ? m.score1 : m.score2;
  const theirScore = isChal1 ? m.score2 : m.score1;
  let result: Result | null = null;
  if (m.status === 'completed' && myScore != null && theirScore != null) {
    result = myScore > theirScore ? 'win' : myScore < theirScore ? 'loss' : 'draw';
  }
  const rs = result ? RESULT_STYLE[result] : null;
  const typeColor = TYPE_COLOR[m.match_type] || '#64748b';
  const accent = rs ? rs.border : typeColor;
  const posterUrl = m.poster_filename ? getMatchPosterUrl(m.poster_filename) : null;
  const myName = isChal1 ? (m.streamer1_name || '?') : (m.streamer2_name || '?');
  const theirName = isChal1 ? (m.streamer2_name || '?') : (m.streamer1_name || '?');

  return (
    <div onClick={onClick} style={{
      background: '#fff', borderRadius: 10, overflow: 'hidden', marginBottom: 10, cursor: 'pointer',
      border: selected ? `2px solid ${accent}` : '1px solid #e2e8f0',
      boxShadow: selected ? `0 0 0 3px ${accent}22` : '0 1px 2px rgba(0,0,0,0.04)',
      display: 'flex', flexDirection: 'column', flexShrink: 0, transition: 'all 0.12s',
    }}>
      {posterUrl ? (
        <img src={posterUrl} alt="" loading="lazy"
          style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block', background: '#0a0a0a' }} />
      ) : (
        <div style={{ width: '100%', height: 90, background: `${typeColor}1f`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40 }}>🥊</div>
      )}
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', lineHeight: 1.3, wordBreak: 'break-word' }}>
          {myName} <span style={{ color: '#94a3b8' }}>vs</span> {theirName}
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>📅 {fmtDateLong(m.scheduled_at)} · {fmtTime(m.scheduled_at)}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: typeColor + '18', color: typeColor }}>
            {TYPE_LABEL[m.match_type] || m.match_label || m.match_type}
          </span>
          {rs && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: rs.color + '15', color: rs.color }}>
              {rs.label}
            </span>
          )}
          {m.status === 'completed' && myScore != null && theirScore != null && (
            <span style={{ fontSize: 12, fontWeight: 800, color: rs ? rs.color : '#0f172a', marginLeft: 'auto' }}>
              {myScore}<span style={{ color: '#cbd5e1' }}>—</span>{theirScore}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function MatchDetail({ m, streamerId, isMobile, onBack }: { m: LiveMatch; streamerId: number; isMobile?: boolean; onBack?: () => void }) {
  const isChal1 = m.streamer1_id === streamerId;
  const myName = isChal1 ? (m.streamer1_name || '?') : (m.streamer2_name || '?');
  const theirName = isChal1 ? (m.streamer2_name || '?') : (m.streamer1_name || '?');
  const myAvatar = isChal1 ? m.streamer1_avatar : m.streamer2_avatar_url;
  const theirAvatar = isChal1 ? m.streamer2_avatar_url : m.streamer1_avatar;
  const theirHandle = (isChal1 ? m.streamer2_username : m.streamer1_username) || '';
  const theirTikTokUrl = theirHandle
    ? `https://www.tiktok.com/@${theirHandle.replace(/^@/, '')}`
    : `https://www.tiktok.com/search?q=${encodeURIComponent(theirName)}`;
  const myScore = isChal1 ? m.score1 : m.score2;
  const theirScore = isChal1 ? m.score2 : m.score1;
  let result: Result | null = null;
  if (m.status === 'completed' && myScore != null && theirScore != null) {
    result = myScore > theirScore ? 'win' : myScore < theirScore ? 'loss' : 'draw';
  }
  const rs = result ? RESULT_STYLE[result] : null;
  const typeColor = TYPE_COLOR[m.match_type] || '#64748b';
  const countdown = m.status === 'scheduled' ? fmtCountdown(m.scheduled_at) : '';
  const posterUrl = m.poster_filename ? getMatchPosterUrl(m.poster_filename) : null;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc' }}>
      {isMobile && onBack && (
        <div style={{ flexShrink: 0, padding: '10px 14px', background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 13, fontWeight: 600, padding: 0 }}>← Retour</button>
        </div>
      )}
      <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? 16 : 28 }}>
        <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {posterUrl && (
            <div style={{ background: '#0a0a0a', borderRadius: 14, overflow: 'hidden', textAlign: 'center' }}>
              <img src={posterUrl} alt="Affiche" style={{ width: '100%', maxHeight: 460, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
            </div>
          )}

          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: isMobile ? 16 : 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Badges en haut */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: typeColor + '18', color: typeColor }}>
                {TYPE_LABEL[m.match_type] || m.match_label || m.match_type}
              </span>
              {rs && (
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: rs.color + '15', color: rs.color }}>
                  {rs.label}
                </span>
              )}
              {m.status === 'scheduled' && countdown && (
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: '#fef3c7', color: '#92400e' }}>
                  ⏰ {countdown}
                </span>
              )}
              {m.forfeit && m.forfeit !== 'none' && (
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: '#fef2f2', color: '#dc2626' }}>⚠ Forfait</span>
              )}
            </div>

            {/* Versus */}
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 20, padding: '6px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                <MiniAvatar url={myAvatar} name={myName} size={isMobile ? 46 : 56} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 800, color: '#0f172a', lineHeight: 1.2, wordBreak: 'break-word' }}>{myName}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Moi</div>
                </div>
              </div>
              <div style={{ textAlign: 'center', flexShrink: 0, minWidth: 80 }}>
                {m.status === 'completed' && myScore != null && theirScore != null ? (
                  <div style={{ fontSize: isMobile ? 28 : 36, fontWeight: 900, color: rs ? rs.color : '#0f172a', letterSpacing: 1, lineHeight: 1 }}>
                    {myScore}<span style={{ color: '#cbd5e1', fontSize: isMobile ? 20 : 26 }}>—</span>{theirScore}
                  </div>
                ) : (
                  <div style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: '#cbd5e1' }}>VS</div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, justifyContent: 'flex-end' }}>
                <div style={{ minWidth: 0, textAlign: 'right' }}>
                  <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 800, color: '#0f172a', lineHeight: 1.2, wordBreak: 'break-word' }}>{theirName}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Adversaire</div>
                </div>
                <MiniAvatar url={theirAvatar} name={theirName} size={isMobile ? 46 : 56} />
              </div>
            </div>

            {/* Date */}
            <div style={{ fontSize: 13, color: '#475569', borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
              📅 {fmtDateLong(m.scheduled_at)} {fmtTime(m.scheduled_at) && `· ${fmtTime(m.scheduled_at)}`}
            </div>

            {/* Contact adversaire — avant un match à venir */}
            {m.status !== 'completed' && (
              <div style={{ border: '1px solid #ddd6fe', background: '#f5f3ff', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#6d28d9' }}>📣 À faire avant le match</div>
                <div style={{ fontSize: 13, color: '#4c1d95', lineHeight: 1.5 }}>
                  <b>Abonne-toi</b> à <b>{theirName}</b> sur TikTok, puis <b>prends contact</b> avec lui/elle pour valider ensemble les <b>modalités du match</b> (date, heure, durée, règles).
                </div>
                <a href={theirTikTokUrl} target="_blank" rel="noreferrer"
                  style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 9, background: '#0f172a', color: '#fff', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
                  <MiniAvatar url={theirAvatar} name={theirName} size={22} /> Voir le profil TikTok de l'adversaire ↗
                </a>
                {!theirHandle && (
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>Le @ exact n'est pas connu — le bouton ouvre une recherche TikTok sur son nom.</div>
                )}
              </div>
            )}

            {/* Téléchargement affiche */}
            {posterUrl && (
              <a href={posterUrl} target="_blank" rel="noreferrer" download={`match_${m.id}_affiche.png`}
                style={{ alignSelf: 'flex-start', padding: '9px 18px', borderRadius: 9, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                📥 Télécharger l'affiche
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function RequestCard({ req, isMobile }: { req: MatchRequest; isMobile: boolean }) {
  const typeColor = TYPE_COLOR[req.match_type] || '#64748b';
  const st = STATUS_STYLE[req.status] || STATUS_STYLE.pending;
  return (
    <div style={{
      background: '#fff', border: '1px solid #e2e8f0',
      borderLeft: `4px solid ${typeColor}`,
      borderRadius: 14, marginBottom: 10,
      padding: isMobile ? '12px 14px' : '14px 20px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: typeColor + '18', color: typeColor }}>
          {TYPE_LABEL[req.match_type] || req.match_label || req.match_type}
        </span>
        {req.match_type === 'autre' && req.match_label && (
          <span style={{ fontSize: 10, color: '#64748b' }}>{req.match_label}</span>
        )}
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: st.bg, color: st.color }}>
          {st.label}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 600 }}>
          📅 {fmtDateLong(req.scheduled_at)} · {fmtTime(req.scheduled_at)}
        </div>
        {req.opponent_name ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <MiniAvatar url={req.opponent_avatar} name={req.opponent_name} size={24} />
            <span style={{ fontSize: 12, color: '#374151' }}>vs {req.opponent_name}</span>
          </div>
        ) : (
          <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>Adversaire à déterminer par l'admin</span>
        )}
      </div>
      {req.admin_notes && (
        <div style={{ marginTop: 8, padding: '6px 10px', background: '#f8fafc', borderRadius: 8, fontSize: 12, color: '#374151' }}>
          💬 {req.admin_notes}
        </div>
      )}
    </div>
  );
}

// ── Modal demande de match ─────────────────────────────────

function RequestModal({ streamerId, streamers, onClose, onCreated }: {
  streamerId: number;
  streamers: Streamer[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultDate = tomorrow.toISOString().slice(0, 10);

  const [type, setType] = useState('officiel');
  const [label, setLabel] = useState('');
  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState('20:00');
  const [opponentId, setOpponentId] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const opponents = streamers.filter(s => s.id !== streamerId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!date || !time) { setError('Veuillez renseigner la date et l\'heure'); return; }
    setSaving(true); setError('');
    try {
      await createMatchRequest({
        match_type: type,
        match_label: type === 'autre' ? (label || null) : null,
        scheduled_at: `${date} ${time}:00`,
        opponent_id: opponentId !== '' ? opponentId : null,
      });
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la demande');
    }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 480, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ background: 'linear-gradient(135deg,#7c3aed,#a855f7)', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>🥊 Demander un match</div>
            <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, marginTop: 2 }}>L'administrateur confirmera votre demande</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, width: 32, height: 32, color: '#fff', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Type */}
          <div>
            <label style={labelStyle}>Type de match</label>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              {(['officiel', 'anniversaire', 'autre'] as const).map(t => (
                <button key={t} type="button" onClick={() => setType(t)}
                  style={{ flex: 1, padding: '8px 6px', borderRadius: 8, border: `2px solid ${type === t ? TYPE_COLOR[t] : '#e2e8f0'}`, background: type === t ? TYPE_COLOR[t] + '12' : '#fff', color: type === t ? TYPE_COLOR[t] : '#374151', fontSize: 11, fontWeight: type === t ? 700 : 400, cursor: 'pointer', transition: 'all 0.12s' }}>
                  {TYPE_LABEL[t]}
                </button>
              ))}
            </div>
          </div>

          {/* Label si autre */}
          {type === 'autre' && (
            <div>
              <label style={labelStyle}>Précisez le type</label>
              <input style={inputStyle} value={label} onChange={e => setLabel(e.target.value)}
                placeholder="Ex : Match Amical, Anniversaire Agence…" maxLength={60} />
            </div>
          )}

          {/* Date + heure */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Date</label>
              <input type="date" style={inputStyle} value={date} onChange={e => setDate(e.target.value)} required />
            </div>
            <div>
              <label style={labelStyle}>Heure</label>
              <input type="time" style={inputStyle} value={time} onChange={e => setTime(e.target.value)} required />
            </div>
          </div>

          {/* Adversaire (optionnel) */}
          <div>
            <label style={labelStyle}>Adversaire <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optionnel — l'admin peut le trouver)</span></label>
            <select style={inputStyle} value={opponentId} onChange={e => setOpponentId(e.target.value !== '' ? Number(e.target.value) : '')}>
              <option value="">— Laisser l'admin choisir —</option>
              {opponents.map(s => (
                <option key={s.id} value={s.id}>{s.display_name}</option>
              ))}
            </select>
          </div>

          {error && (
            <div style={{ padding: '8px 12px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="button" onClick={onClose}
              style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#374151', fontSize: 13, cursor: 'pointer' }}>
              Annuler
            </button>
            <button type="submit" disabled={saving}
              style={{ padding: '9px 24px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Envoi…' : '📤 Envoyer la demande'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#374151' };
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1px solid #e2e8f0', background: '#fff',
  color: '#0f172a', fontSize: 13, boxSizing: 'border-box', outline: 'none', marginTop: 4,
};

// ── Composant principal ────────────────────────────────────

export default function MesMatchs({ streamerId, isMobile }: { streamerId: number; isMobile: boolean }) {
  const [matches, setMatches] = useState<LiveMatch[]>([]);
  const [requests, setRequests] = useState<MatchRequest[]>([]);
  const [streamers, setStreamers] = useState<Streamer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'scheduled' | 'completed'>('all');
  const [showRequests, setShowRequests] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Rappels SMS avant match (voir CODEX §30)
  const [reminder, setReminder] = useState<SmsReminder | null>(null);
  const [phoneInput, setPhoneInput] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const [savingReminder, setSavingReminder] = useState(false);

  useEffect(() => {
    getSmsReminder(streamerId).then(r => setReminder(r.data)).catch(() => {});
  }, [streamerId]);

  const savePhone = async () => {
    const p = phoneInput.trim();
    if (!p) return;
    setSavingPhone(true);
    try {
      await updateProfile({ phone: p });
      const r = await getSmsReminder(streamerId);
      setReminder(r.data);
      setPhoneInput('');
    } catch {}
    setSavingPhone(false);
  };

  const updateReminder = async (patch: { enabled?: boolean; lead_min?: number }) => {
    setSavingReminder(true);
    try {
      const r = await setSmsReminder(streamerId, patch);
      setReminder(r.data);
    } catch {}
    setSavingReminder(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [mRes, rRes, sRes] = await Promise.all([getMatches(), getMatchRequests(), getStreamers()]);
      const mine = mRes.data.filter(m => m.streamer1_id === streamerId || m.streamer2_id === streamerId);
      setMatches(mine);
      setRequests(rRes.data.filter(r => r.requester_id === streamerId));
      setStreamers(sRes.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [streamerId]); // eslint-disable-line

  const pendingReqs = requests.filter(r => r.status === 'pending');
  // Les demandes ACCEPTÉES deviennent des matchs → inutile de les afficher en historique
  const otherReqs = requests.filter(r => r.status !== 'pending' && r.status !== 'accepted');

  const q = search.trim().toLowerCase();
  const filtered = matches
    .filter(m => filter === 'all' ? true : m.status === filter)
    .filter(m => !q
      || (m.streamer1_name || '').toLowerCase().includes(q)
      || (m.streamer2_name || '').toLowerCase().includes(q)
      || (m.match_label || '').toLowerCase().includes(q))
    .sort((a, b) => filter === 'completed'
      ? b.scheduled_at.localeCompare(a.scheduled_at)
      : a.scheduled_at.localeCompare(b.scheduled_at));

  const selected = selectedId != null ? matches.find(m => m.id === selectedId) || null : null;
  const showLeft = !isMobile || !selected;
  const showRight = !isMobile || !!selected;

  const leftPanel = (
    <div style={{ width: isMobile ? '100%' : 340, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: isMobile ? 'none' : '1px solid #e2e8f0', height: '100%', background: '#f8fafc' }}>
      <div style={{ flexShrink: 0, background: '#fff', borderBottom: '1px solid #e2e8f0', padding: isMobile ? '12px 14px' : '14px 16px' }}>
        <h1 style={{ margin: 0, color: '#0f172a', fontSize: isMobile ? 17 : 18, fontWeight: 700 }}>🥊 Mes Matchs</h1>
      </div>
      {reminder && (
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #e2e8f0', background: '#fff', flexShrink: 0 }}>
          {!reminder.phone ? (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 10 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>
                📱 Reçois tes rappels de match par SMS
              </div>
              <div style={{ fontSize: 11.5, color: '#a16207', marginBottom: 8, lineHeight: 1.4 }}>
                Ajoute ton numéro de mobile pour être prévenu avant chaque match.
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input value={phoneInput} onChange={e => setPhoneInput(e.target.value)} type="tel"
                  placeholder="+33 6 12 34 56 78"
                  style={{ flex: 1, padding: '7px 10px', borderRadius: 8, border: '1px solid #fcd34d', fontSize: 13, outline: 'none', background: '#fff', boxSizing: 'border-box' }} />
                <button onClick={savePhone} disabled={savingPhone || !phoneInput.trim()}
                  style={{ padding: '7px 12px', borderRadius: 8, border: 'none', background: '#f59e0b', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', opacity: (savingPhone || !phoneInput.trim()) ? 0.6 : 1 }}>
                  {savingPhone ? '…' : 'Enregistrer'}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={() => updateReminder({ enabled: !reminder.enabled })} disabled={savingReminder}
                style={{ display: 'flex', alignItems: 'center', gap: 8, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
                <span style={{ width: 38, height: 22, borderRadius: 999, background: reminder.enabled ? '#16a34a' : '#cbd5e1', position: 'relative', transition: 'background .15s', flexShrink: 0, display: 'inline-block' }}>
                  <span style={{ position: 'absolute', top: 2, left: reminder.enabled ? 18 : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
                </span>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: '#0f172a' }}>🔔 Rappel SMS</span>
              </button>
              {reminder.enabled && (
                <select value={reminder.lead_min} onChange={e => updateReminder({ lead_min: Number(e.target.value) })} disabled={savingReminder}
                  title="Combien de temps avant le match"
                  style={{ marginLeft: 'auto', padding: '5px 8px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 12, background: '#fff', cursor: 'pointer', color: '#0f172a' }}>
                  {[15, 30, 60, 120, 180].map(v => <option key={v} value={v}>{v < 60 ? `${v} min avant` : `${v / 60} h avant`}</option>)}
                </select>
              )}
            </div>
          )}
        </div>
      )}
      <div style={{ padding: '12px 14px', borderBottom: '1px solid #e2e8f0', background: '#fff', display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un match…"
          style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#f8fafc', boxSizing: 'border-box', outline: 'none' }} />
        <div style={{ display: 'flex', gap: 6 }}>
          {(['all', 'scheduled', 'completed'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ flex: 1, padding: '6px 0', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: filter === f ? 700 : 500, cursor: 'pointer',
                background: filter === f ? '#7c3aed' : '#f1f5f9',
                color: filter === f ? '#fff' : '#64748b' }}>
              {f === 'all' ? 'Tous' : f === 'scheduled' ? '🗓 À venir' : '✅ Terminés'}
            </button>
          ))}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
        {loading && <div style={{ textAlign: 'center', color: '#94a3b8', padding: 32, fontSize: 13 }}>Chargement…</div>}
        {!loading && (
          <>
            {pendingReqs.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <button onClick={() => setShowRequests(v => !v)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, border: '1px solid #fde68a', background: '#fffbeb', cursor: 'pointer', marginBottom: showRequests ? 8 : 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>📬 Demandes en attente</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ background: '#f59e0b', color: '#fff', borderRadius: 20, fontSize: 10, fontWeight: 700, padding: '1px 7px' }}>{pendingReqs.length}</span>
                    <span style={{ color: '#92400e', fontSize: 12 }}>{showRequests ? '▲' : '▼'}</span>
                  </span>
                </button>
                {showRequests && pendingReqs.map(r => <RequestCard key={r.id} req={r} isMobile={isMobile} />)}
              </div>
            )}

            {filtered.length === 0 && matches.length === 0 && requests.length === 0 && (
              <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 16px' }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>🥊</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Aucun match</div>
                <div style={{ fontSize: 12, lineHeight: '1.5' }}>Fais une demande de match&nbsp;!</div>
              </div>
            )}
            {filtered.length === 0 && matches.length > 0 && (
              <div style={{ textAlign: 'center', color: '#94a3b8', padding: 24, fontSize: 12 }}>
                {q ? 'Aucun résultat.' : 'Aucun match dans ce filtre.'}
              </div>
            )}
            {filtered.map(m => (
              <MatchCard key={m.id} m={m} streamerId={streamerId}
                selected={selectedId === m.id} onClick={() => setSelectedId(m.id)} />
            ))}

            {otherReqs.length > 0 && (
              <div style={{ marginTop: 18 }}>
                <h2 style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Historique des demandes</h2>
                {otherReqs.map(r => <RequestCard key={r.id} req={r} isMobile={isMobile} />)}
              </div>
            )}
          </>
        )}
      </div>
      <div style={{ flexShrink: 0, padding: 12, borderTop: '1px solid #e2e8f0', background: '#fff' }}>
        <button onClick={() => setShowModal(true)}
          style={{ width: '100%', padding: '11px 0', borderRadius: 10, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          🥊 Demander un match
        </button>
      </div>
    </div>
  );

  const rightPanel = selected ? (
    <MatchDetail m={selected} streamerId={streamerId} isMobile={isMobile} onBack={() => setSelectedId(null)} />
  ) : (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', background: '#f8fafc' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🥊</div>
        <div style={{ fontSize: 14 }}>Sélectionne un match à gauche</div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden', background: '#f8fafc' }}>
      {showLeft && leftPanel}
      {showRight && rightPanel}

      {showModal && (
        <RequestModal
          streamerId={streamerId}
          streamers={streamers}
          onClose={() => setShowModal(false)}
          onCreated={load}
        />
      )}
    </div>
  );
}
